// context/SavedUnitsContext.tsx
/**
 * @description Context for managing saved units with localStorage persistence
 * Replaces CompareContext with simpler "Save" paradigm
 */
import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { Unit } from '../types/database';

const MAX_SAVED_UNITS = 5;
const STORAGE_KEY = 'beaux-saved-units';

interface SavedUnitsContextType {
  // State
  savedUnits: Unit[];
  count: number;
  isSaved: (unitId: string) => boolean;
  canSaveMore: boolean;

  // Actions
  saveUnit: (unit: Unit) => boolean;
  removeUnit: (unitId: string) => void;
  toggleSave: (unit: Unit) => boolean; // Returns true if saved, false if removed
  clearAll: () => void;
}

const SavedUnitsContext = createContext<SavedUnitsContextType | undefined>(undefined);

interface SavedUnitsProviderProps {
  children: ReactNode;
}

export function SavedUnitsProvider({ children }: SavedUnitsProviderProps) {
  const [savedUnits, setSavedUnits] = useState<Unit[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(savedUnits));
    } catch (e) {
      console.error('Failed to save units to localStorage:', e);
    }
  }, [savedUnits]);

  const isSaved = useCallback(
    (unitId: string) => savedUnits.some((u) => u.id === unitId),
    [savedUnits]
  );

  const canSaveMore = savedUnits.length < MAX_SAVED_UNITS;
  const count = savedUnits.length;

  const saveUnit = useCallback(
    (unit: Unit): boolean => {
      if (savedUnits.length >= MAX_SAVED_UNITS) {
        return false;
      }
      if (savedUnits.some((u) => u.id === unit.id)) {
        return false;
      }
      setSavedUnits((prev) => [...prev, unit]);
      return true;
    },
    [savedUnits]
  );

  const removeUnit = useCallback((unitId: string) => {
    setSavedUnits((prev) => prev.filter((u) => u.id !== unitId));
  }, []);

  const toggleSave = useCallback(
    (unit: Unit): boolean => {
      if (savedUnits.some((u) => u.id === unit.id)) {
        removeUnit(unit.id);
        return false;
      } else {
        return saveUnit(unit);
      }
    },
    [savedUnits, removeUnit, saveUnit]
  );

  const clearAll = useCallback(() => {
    setSavedUnits([]);
  }, []);

  const value: SavedUnitsContextType = {
    savedUnits,
    count,
    isSaved,
    canSaveMore,
    saveUnit,
    removeUnit,
    toggleSave,
    clearAll,
  };

  return (
    <SavedUnitsContext.Provider value={value}>{children}</SavedUnitsContext.Provider>
  );
}

export function useSavedUnits(): SavedUnitsContextType {
  const context = useContext(SavedUnitsContext);
  if (!context) {
    throw new Error('useSavedUnits must be used within a SavedUnitsProvider');
  }
  return context;
}
